import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowForward,
  CalendarToday,
  Inventory2Outlined,
} from '@mui/icons-material';
import { lowerCaseAllWordsExceptFirstLetters } from '../../../../api/helper';
import { getCountryFlag } from '../../utils/countryFlags';
import './CourierCard.css';

interface CourierCardProps {
  courier: {
    id: number;
    courierType: string;
    creator: { id: number; name: string };
    sourceCity: string;
    sourceCountry: string;
    sourceCountryCode: string | null;
    destinationCity: string;
    destinationCountry: string;
    destinationCountryCode: string | null;
    size: { name: string };
    details: string;
    expiresAt: string;
    active: boolean;
  };
  index: number;
  onClick: () => void;
  renderActions: () => React.ReactNode;
  isInactive: boolean;
  isUserCourier: boolean;
}

const CourierCard: React.FC<CourierCardProps> = ({
  courier,
  index,
  onClick,
  renderActions,
  isInactive,
  isUserCourier,
}) => {
  const theme = useTheme();
  const isSender = courier.courierType === 'SENDER';

  const cardStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
    padding: 3,
    height: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.editorial.paperGray}`,
    borderTop: courier.active
      ? `3px solid ${isSender ? theme.palette.courier.main : theme.palette.primary.main}`
      : `3px solid ${theme.palette.editorial.paperGray}`,
    cursor: 'pointer',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isInactive ? 0.6 : 1,
    filter: isInactive ? 'grayscale(0.5)' : 'none',
    position: 'relative',
    overflow: 'hidden',
    '&::before': !isInactive ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${isSender ? theme.palette.courier.main : theme.palette.primary.main}, ${isSender ? theme.palette.courier.light : theme.palette.secondary.main})`,
      transform: 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    } : {},
    '&:hover': !isInactive
      ? {
          transform: 'translateY(-6px) scale(1.01)',
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.15)',
          borderColor: isSender ? theme.palette.courier.main : theme.palette.primary.main,
          '&::before': {
            transform: 'scaleX(1)',
          },
        }
      : {},
    '&:active': !isInactive
      ? {
          transform: 'translateY(-2px) scale(1)',
        }
      : {},
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box
      sx={cardStyles}
      onClick={onClick}
      className="courier-card"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Main Content Section */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Chip
            label={lowerCaseAllWordsExceptFirstLetters(courier.courierType)}
            size="small"
            sx={{
              backgroundColor: isSender
                ? alpha(theme.palette.courier.main, 0.1)
                : alpha(theme.palette.primary.main, 0.1),
              color: isSender
                ? theme.palette.courier.dark
                : theme.palette.primary.main,
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderRadius: '6px',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: isSender
                  ? theme.palette.courier.main
                  : theme.palette.primary.main,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {getInitials(courier.creator.name)}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {courier.creator.name}
            </Typography>
          </Box>
          <Chip
            label={courier.active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              backgroundColor: courier.active
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.text.secondary, 0.1),
              color: courier.active
                ? theme.palette.success.dark
                : theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: '6px',
            }}
          />
        </Box>

        {/* Route Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: '0.625rem',
                color: theme.palette.text.secondary,
                lineHeight: 1,
              }}
            >
              FROM
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getCountryFlag(courier.sourceCountryCode) && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    lineHeight: 1,
                  }}
                >
                  {getCountryFlag(courier.sourceCountryCode)}
                </Typography>
              )}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  color: theme.palette.text.primary,
                }}
              >
                {courier.sourceCity}, {courier.sourceCountry}
              </Typography>
            </Box>
          </Box>
          <ArrowForward
            className="route-arrow"
            sx={{
              color: isSender
                ? theme.palette.courier.main
                : theme.palette.primary.main,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              flexShrink: 0,
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: '0.625rem',
                color: theme.palette.text.secondary,
                lineHeight: 1,
              }}
            >
              TO
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getCountryFlag(
                courier.destinationCountryCode || courier.sourceCountryCode
              ) && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    lineHeight: 1,
                  }}
                >
                  {getCountryFlag(
                    courier.destinationCountryCode || courier.sourceCountryCode
                  )}
                </Typography>
              )}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  color: theme.palette.text.primary,
                }}
              >
                {courier.destinationCity},{' '}
                {courier.destinationCountry || courier.sourceCountry}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Meta Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, md: 3 },
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2Outlined
              sx={{
                fontSize: '1.125rem',
                color: theme.palette.text.secondary,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              {courier.size.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday
              sx={{
                fontSize: '1rem',
                color: theme.palette.text.secondary,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Until: {courier.expiresAt}
            </Typography>
          </Box>
        </Box>

        {/* Details Row */}
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {courier.details}
        </Typography>
      </Box>

      {/* Actions Section - Bottom of card */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pt: 2,
          mt: 'auto',
          borderTop: `1px solid ${theme.palette.editorial.paperGray}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {renderActions()}
      </Box>
    </Box>
  );
};

export default CourierCard;
